import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listenToChannel,
  sendToChannel,
  isInIframe,
  type ChannelMessage,
  type AuthRequestMessage,
  type ContractMessage,
} from '../utils/iframeChannel';
import { useIdentity } from './useIdentity';
import { signMessage, generateChallenge } from '../utils/crypto';
import { GraphResolver } from '../services/graphResolver';
import { updateGraphFromMessage } from '../services/syncUpdateGraph';
import type { LoginProof } from '../types/identity';
import type { Contrat, Action } from '../types/contract';

export function useChannel() {
  const navigate = useNavigate();
  const { identity } = useIdentity();
  const graphResolver = useState(() => {
    return new GraphResolver();
  })[0];

  const handleAuthRequest = useCallback(
    (_message: AuthRequestMessage): void => {
      if (identity?.privateKey === undefined) {
        sendToChannel({
          type: 'error',
          payload: {
            message:
              identity === null
                ? 'Identité non disponible'
                : 'Identité verrouillée. Déverrouillez d\'abord.',
            code: 'NO_IDENTITY',
          },
        });
        return;
      }

      const challenge = generateChallenge();
      const signature = signMessage(challenge, identity.privateKey);

      sendToChannel({
        type: 'auth-response',
        payload: {
          signature,
          publicKey: identity.publicKey,
          message: challenge,
        },
      });
    },
    [identity],
  );

  const handleContract = useCallback(
    (message: ContractMessage): void => {
      const payload = message.payload;

      if (payload?.contrat !== undefined) {
        // Valider et ajouter le contrat principal
        try {
          const contrat = payload.contrat as Contrat;
          if (
            typeof contrat.uuid === 'string' &&
            typeof contrat.version === 'string' &&
            typeof contrat.validateurs === 'object' &&
            contrat.validateurs !== null
          ) {
            updateGraphFromMessage(contrat, graphResolver);
          } else {
            console.warn(
              'Invalid contract structure received via channel: missing required fields',
            );
          }
        } catch (err) {
          console.error('Error processing contract from channel:', err);
        }
      }

      // Ajouter les contrats fils
      if (Array.isArray(payload?.contrats_fils)) {
        for (const cf of payload.contrats_fils) {
          try {
            const contrat = cf as Contrat;
            if (
              typeof contrat.uuid === 'string' &&
              typeof contrat.version === 'string'
            ) {
              updateGraphFromMessage(contrat, graphResolver);
            } else {
              console.warn(
                'Invalid child contract structure received via channel',
              );
            }
          } catch (err) {
            console.error('Error processing child contract from channel:', err);
          }
        }
      }

      // Ajouter les actions
      if (Array.isArray(payload?.actions)) {
        for (const a of payload.actions) {
          try {
            const action = a as Action;
            if (
              typeof action.uuid === 'string' &&
              typeof action.validateurs_action === 'object' &&
              action.validateurs_action !== null
            ) {
              updateGraphFromMessage(action, graphResolver);
            } else {
              console.warn('Invalid action structure received via channel');
            }
          } catch (err) {
            console.error('Error processing action from channel:', err);
          }
        }
      }

    },
    [graphResolver],
  );

  const handleNavigateLogin = useCallback(
    (message: ChannelMessage): void => {
      if (message.type !== 'navigate-login') {
        return;
      }
      const payload = message.payload as { service?: string; member?: string };
      const service =
        typeof payload?.service === 'string' ? payload.service : '';
      const member =
        typeof payload?.member === 'string' ? payload.member : '';
      const params = new URLSearchParams();
      if (service !== '') {
        params.set('service', service);
      }
      if (member !== '') {
        params.set('membre', member);
      }
      const qs = params.toString();
      navigate(qs !== '' ? `/login?${qs}` : '/login');
    },
    [navigate],
  );

  const sendLoginProof = useCallback((proof: LoginProof): void => {
    sendToChannel({
      type: 'login-proof',
      payload: proof,
    });
  }, []);

  useEffect(() => {
    if (!isInIframe()) {
      return;
    }

    const cleanup = listenToChannel((message: ChannelMessage) => {
      if (message.type === 'auth-request') {
        handleAuthRequest(message as AuthRequestMessage);
      } else if (message.type === 'contract') {
        handleContract(message as ContractMessage);
      } else if (message.type === 'navigate-login') {
        handleNavigateLogin(message);
      }
    });

    return cleanup;
  }, [handleAuthRequest, handleContract, handleNavigateLogin]);

  return {
    sendLoginProof,
    isInIframe: isInIframe(),
  };
}
